import dishaChatService from '@/services/dishaChatService';
import {
  normalizeChatMessage,
  normalizeChatMessages,
  dedupeTaskMessages
} from '@/helper/dishaChatMessageUtils';

/** Keep real read receipts only — no mock "Seen by 2" data. */
function enrichMessageWithReceipts(message) {
  if (!message || message.type === 'system' || message.type === 'task') {
    return message;
  }
  let inferredDeliveryStatus = 'sent';
  if (message.readBy?.length) {
    inferredDeliveryStatus = 'read';
  } else if (message.deliveredAt) {
    inferredDeliveryStatus = 'delivered';
  }

  return {
    ...message,
    deliveryStatus: message.deliveryStatus || inferredDeliveryStatus,
    readBy: Array.isArray(message.readBy) ? message.readBy : []
  };
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.conversations)) return payload.conversations;
  if (Array.isArray(payload?.messages)) return payload.messages;
  if (Array.isArray(payload?.tickets)) return payload.tickets;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  return [];
}

function unwrapData(response) {
  return response?.data ?? response;
}

function getAuthUser(rootState) {
  return rootState.auth?.user || null;
}

function getUserId(user) {
  if (!user) return null;
  return user.id || user._id || user.userId || null;
}

function getUserDisplayParts(user) {
  const firstName =
    user?.firstName || (user?.name ? String(user.name).split(' ')[0] : '') || '';
  const lastName =
    user?.lastName ||
    (user?.name ? String(user.name).split(' ').slice(1).join(' ') : '') ||
    '';
  return { firstName, lastName };
}

function getErrorMessage(e, fallback = 'Something went wrong') {
  return (
    e?.message ||
    e?.response?.data?.message ||
    e?.data?.message ||
    fallback
  );
}

function handleActionError({ commit, dispatch }, { e, router, fallback }) {
  const message = getErrorMessage(e, fallback);
  if (router) {
    dispatch('common/handleServiceException', { e, router }, { root: true });
  } else {
    commit('setError', message);
  }
}

function messageAlreadyExists(list, message) {
  return list.some(m => {
    if (message.id && m.id && String(m.id) === String(message.id)) return true;
    if (
      message.type === 'task' &&
      m.type === 'task' &&
      message.task?.id &&
      m.task?.id &&
      String(m.task.id) === String(message.task.id)
    ) {
      return true;
    }
    return false;
  });
}

function softDeleteFields(message = {}) {
  return {
    ...message,
    isDeleted: true,
    body: '',
    content: '',
    files: [],
    attachments: [],
    mentions: []
  };
}

function applySoftDeleteToConversation(state, conversationId, messageId, nextMessage) {
  const conv = state.conversations.find(c => String(c.id) === String(conversationId));
  if (conv?.lastMessage && String(conv.lastMessage.id) === String(messageId)) {
    conv.lastMessage = nextMessage || softDeleteFields(conv.lastMessage);
  }
}

function pushMessageIfNew(state, message) {
  const enriched = enrichMessageWithReceipts(normalizeChatMessage(message));
  const convId = enriched.conversationId;
  if (!convId) return;

  if (!state.messages[convId]) {
    state.messages[convId] = [];
  }

  const existingIndex = state.messages[convId].findIndex(
    m => enriched.id && m.id && String(m.id) === String(enriched.id)
  );

  if (existingIndex >= 0) {
    if (enriched.isDeleted) {
      const deleted = softDeleteFields({
        ...state.messages[convId][existingIndex],
        ...enriched
      });
      state.messages[convId].splice(existingIndex, 1, deleted);
      applySoftDeleteToConversation(state, convId, enriched.id, deleted);
    }
    return;
  }

  if (messageAlreadyExists(state.messages[convId], enriched)) return;

  state.messages[convId].push(enriched);

  const conv = state.conversations.find(c => String(c.id) === String(convId));
  if (conv) {
    conv.lastMessage = enriched;
  }
}

const initialState = () => ({
  conversations: [],
  activeConversationId: null,
  messages: {},
  messagePages: {},
  hasMoreMessages: {},
  loadingMoreMessages: false,
  unreadCounts: {},
  typingUsers: {},
  mentions: [],
  mentionsLoading: false,
  connected: false,
  loading: false,
  error: null,
  activeTab: 'team',
  aiMessages: [],
  aiConversationId: null,
  aiLoading: false,
  aiConversations: [],
  aiConversationsLoading: false,
  pinnedChatIds: [],
  chatListFilter: 'all',
  aiDraftReply: { text: '', loading: false },
  assistantData: null,
  assistantLoading: false,
  notifyLoading: false,
  nudgeLoading: false,
  teamUsers: [],
  unrestrictedUsers: []
});

const state = initialState();

const mutations = {
  resetState(state) {
    Object.assign(state, initialState());
  },

  setConversations(state, conversations) {
    const list = conversations || [];
    state.conversations = list;
    state.pinnedChatIds = list.filter(c => c?.pinned === true).map(c => c.id);
  },

  setActiveConversation(state, conversationId) {
    state.activeConversationId = conversationId;
    if (conversationId) {
      const conv = state.conversations.find(c => String(c.id) === String(conversationId));
      if (conv) conv.unreadCount = 0;
      state.unreadCounts[conversationId] = 0;
    }
  },

  setConnected(state, connected) {
    state.connected = connected;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setLoadingMore(state, loading) {
    state.loadingMoreMessages = loading;
  },

  setError(state, error) {
    state.error = error;
  },

  setMessagesForConversation(state, { conversationId, messages, page, hasMore }) {
    const normalized = Array.isArray(messages)
      ? dedupeTaskMessages(
        messages.map(msg => enrichMessageWithReceipts(normalizeChatMessage(msg)))
      )
      : [];
    state.messages[conversationId] = normalized;
    if (page != null) state.messagePages[conversationId] = page;
    if (hasMore != null) state.hasMoreMessages[conversationId] = hasMore;
  },

  appendOlderMessages(state, { conversationId, messages, page, hasMore }) {
    const normalizedRows = Array.isArray(messages)
      ? dedupeTaskMessages(
        messages.map(msg => enrichMessageWithReceipts(normalizeChatMessage(msg)))
      )
      : [];

    if (state.messages[conversationId]?.length) {
      const existing = state.messages[conversationId];
      const existingIds = new Set(existing.map(m => String(m.id)));
      const existingTaskIds = new Set(
        existing
          .filter(m => m?.type === 'task' && m?.task?.id != null)
          .map(m => String(m.task.id))
      );
      const older = normalizedRows.filter(m => {
        if (m?.id && existingIds.has(String(m.id))) return false;
        if (
          m?.type === 'task' &&
          m?.task?.id != null &&
          existingTaskIds.has(String(m.task.id))
        ) {
          return false;
        }
        return true;
      });
      state.messages[conversationId] = dedupeTaskMessages([...older, ...existing]);
    } else {
      state.messages[conversationId] = normalizedRows;
    }

    if (page != null) state.messagePages[conversationId] = page;
    if (hasMore != null) state.hasMoreMessages[conversationId] = hasMore;
  },

  messageReceived(state, payload) {
    pushMessageIfNew(state, payload);
  },

  removeMessage(state, { conversationId, messageId }) {
    if (!state.messages[conversationId]) return;
    state.messages[conversationId] = state.messages[conversationId].filter(
      m => String(m.id) !== String(messageId)
    );
  },

  /** Soft-delete — keep a tombstone so the thread shows "This message was deleted". */
  markMessageDeleted(state, { conversationId, messageId }) {
    if (!conversationId || !messageId || !state.messages[conversationId]) return;

    state.messages[conversationId] = state.messages[conversationId].map(m =>
      String(m.id) === String(messageId) ? softDeleteFields(m) : m
    );
    applySoftDeleteToConversation(state, conversationId, messageId);
  },

  updateTaskMessageStatus(state, { ticketId, status, title, assignee }) {
    if (!ticketId) return;

    Object.keys(state.messages).forEach(convId => {
      let updatedLast = null;
      state.messages[convId] = state.messages[convId].map(msg => {
        if (msg?.type === 'task' && String(msg?.task?.id) === String(ticketId)) {
          const next = {
            ...msg,
            task: {
              ...msg.task,
              ...(status != null ? { status } : {}),
              ...(title != null ? { title } : {}),
              ...(assignee != null ? { assignee } : {})
            },
            metadata: {
              ...(msg.metadata || {}),
              ...(status != null ? { status } : {}),
              ...(title != null ? { title } : {}),
              ...(assignee != null ? { assignee } : {}),
              task: {
                ...(msg.metadata?.task || msg.task || {}),
                ...(status != null ? { status } : {}),
                ...(title != null ? { title } : {}),
                ...(assignee != null ? { assignee } : {})
              }
            }
          };
          updatedLast = next;
          return next;
        }
        return msg;
      });

      if (updatedLast) {
        const conv = state.conversations.find(c => String(c.id) === String(convId));
        if (
          conv?.lastMessage?.type === 'task' &&
          String(conv.lastMessage?.task?.id) === String(ticketId)
        ) {
          conv.lastMessage = updatedLast;
        }
      }
    });
  },

  unreadUpdated(state, { conversationId, count }) {
    state.unreadCounts[conversationId] = count;
    const conv = state.conversations.find(c => String(c.id) === String(conversationId));
    if (conv) conv.unreadCount = count;
  },

  typingStarted(state, { conversationId, userId }) {
    if (!state.typingUsers[conversationId]) {
      state.typingUsers[conversationId] = [];
    }
    if (!state.typingUsers[conversationId].includes(userId)) {
      state.typingUsers[conversationId].push(userId);
    }
  },

  typingStopped(state, { conversationId, userId }) {
    if (state.typingUsers[conversationId]) {
      state.typingUsers[conversationId] = state.typingUsers[conversationId].filter(
        id => id !== userId
      );
    }
  },

  conversationUpdated(state, updated) {
    if (!updated?.id) return;
    state.conversations = state.conversations.map(c =>
      String(c.id) === String(updated.id) ? { ...c, ...updated } : c
    );
  },

  updateConversationInList(state, updated) {
    if (!updated?.id) return;

    state.conversations = state.conversations.map(c => {
      if (String(c.id) !== String(updated.id)) return c;

      const next = { ...c, ...updated };

      if (updated.photoFileId && !updated.photo) {
        next.photo = {
          ...(typeof c.photo === 'object' ? c.photo : {}),
          path: updated.photoFileId,
          version: Date.now()
        };
      } else if (updated.photo) {
        next.photo = {
          ...(typeof updated.photo === 'object' ? updated.photo : { path: updated.photo }),
          version: updated.photo?.version || Date.now()
        };
      }

      return next;
    });
  },

  updateParticipants(state, { conversationId, data, userId, role }) {
    const updated = data?.data || data;
    state.conversations = state.conversations.map(c => {
      if (String(c.id) !== String(conversationId)) return c;
      if (updated?.participants) return { ...c, ...updated };
      if (Array.isArray(updated)) return { ...c, participants: updated };
      if (userId != null && role != null) {
        return {
          ...c,
          participants: (c.participants || []).map(p => {
            const pid = p.userId || p.user?.id || p.id;
            if (Number(pid) !== Number(userId)) return p;
            return { ...p, role };
          })
        };
      }
      if (userId != null) {
        return {
          ...c,
          participants: (c.participants || []).filter(
            p => Number(p.userId || p.user?.id || p.id) !== Number(userId)
          )
        };
      }
      return c;
    });
  },

  removeConversation(state, conversationId) {
    state.conversations = state.conversations.filter(
      c => String(c.id) !== String(conversationId)
    );
    state.pinnedChatIds = state.pinnedChatIds.filter(
      id => String(id) !== String(conversationId)
    );
    if (String(state.activeConversationId) === String(conversationId)) {
      state.activeConversationId = state.conversations[0]?.id || null;
    }
    delete state.messages[conversationId];
    delete state.messagePages[conversationId];
    delete state.hasMoreMessages[conversationId];
    delete state.unreadCounts[conversationId];
    delete state.typingUsers[conversationId];
  },

  mentionReceived(state, mention) {
    state.mentions.unshift(mention);
  },

  setMentions(state, mentions) {
    state.mentions = mentions || [];
  },

  setMentionsLoading(state, loading) {
    state.mentionsLoading = loading;
  },

  setAiConversationsLoading(state, loading) {
    state.aiConversationsLoading = loading;
  },

  setActiveTab(state, tab) {
    state.activeTab = tab;
  },

  aiMessageReceived(state, message) {
    state.aiMessages.push(message);
  },

  setAiMessages(state, messages) {
    state.aiMessages = messages || [];
  },

  setAiConversationId(state, id) {
    state.aiConversationId = id;
  },

  setAiLoading(state, loading) {
    state.aiLoading = loading;
  },

  startNewAiChat(state) {
    state.aiMessages = [];
    state.aiConversationId = null;
  },

  setAiConversations(state, conversations) {
    state.aiConversations = conversations || [];
  },

  setChatListFilter(state, filter) {
    state.chatListFilter = filter;
  },

  setPinnedChatIds(state, ids) {
    state.pinnedChatIds = ids || [];
  },

  setAiDraftReplyText(state, text) {
    state.aiDraftReply.text = text;
  },

  setAiDraftLoading(state, loading) {
    state.aiDraftReply.loading = loading;
  },

  clearAiDraftReply(state) {
    state.aiDraftReply = { text: '', loading: false };
  },

  appendSystemMessage(state, message) {
    const convId = message.conversationId;
    if (!convId) return;
    if (!state.messages[convId]) state.messages[convId] = [];
    state.messages[convId].push(message);
    const conv = state.conversations.find(c => String(c.id) === String(convId));
    if (conv) conv.lastMessage = message;
  },

  updateMessageReadReceipt(state, { conversationId, messageId, readBy }) {
    const msgs = state.messages[conversationId];
    if (!msgs) return;
    const idx = msgs.findIndex(m => String(m.id) === String(messageId));
    if (idx >= 0) {
      msgs[idx] = {
        ...msgs[idx],
        readBy,
        deliveryStatus: 'read'
      };
    }
  },

  setNotifyLoading(state, loading) {
    state.notifyLoading = loading;
  },

  setNudgeLoading(state, loading) {
    state.nudgeLoading = loading;
  },

  setAssistantData(state, data) {
    state.assistantData = data;
  },

  setAssistantLoading(state, loading) {
    state.assistantLoading = loading;
  },

  setTeamUsers(state, users) {
    state.teamUsers = users || [];
  },

  setUnrestrictedUsers(state, users) {
    state.unrestrictedUsers = users || [];
  }
};

const getters = {
  activeConversation(state) {
    if (!state.activeConversationId) return null;
    return (
      state.conversations.find(
        c => String(c.id) === String(state.activeConversationId)
      ) || null
    );
  },

  activeMessages(state) {
    if (!state.activeConversationId) return [];
    return state.messages[state.activeConversationId] || [];
  },

  activeTypingUsers(state) {
    if (!state.activeConversationId) return [];
    return state.typingUsers[state.activeConversationId] || [];
  },

  isLoading(state) {
    return state.loading || state.loadingMoreMessages;
  }
};

const actions = {
  reset({ commit }) {
    commit('resetState');
  },

  async fetchConversations({ commit, dispatch }, { router, ...params } = {}) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await dishaChatService.listConversations(params);
      const list = extractList(unwrapData(response));
      commit('setConversations', list);
      return list;
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch conversations'
      });
      return [];
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchMessages(
    { commit, dispatch, state },
    { conversationId, params = {}, append = false, router } = {}
  ) {
    if (!conversationId) return [];

    const limit = params.limit || 40;
    const page = params.page || 1;

    if (append) commit('setLoadingMore', true);
    else commit('setLoading', true);
    commit('setError', null);

    try {
      const response = await dishaChatService.listMessages(conversationId, {
        ...params,
        page,
        limit
      });
      const raw = unwrapData(response);
      const rows = extractList(raw);
      const chronological = [...rows].reverse();
      const normalized = normalizeChatMessages(chronological);

      let hasMore = rows.length >= limit;
      if (typeof response?.hasMore === 'boolean') {
        hasMore = response.hasMore;
      } else if (typeof raw?.hasMore === 'boolean') {
        hasMore = raw.hasMore;
      }

      if (append && state.messages[conversationId]?.length) {
        commit('appendOlderMessages', {
          conversationId,
          messages: normalized,
          page,
          hasMore
        });
      } else {
        commit('setMessagesForConversation', {
          conversationId,
          messages: normalized,
          page,
          hasMore
        });
      }

      return normalized;
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch messages'
      });
      return [];
    } finally {
      commit('setLoading', false);
      commit('setLoadingMore', false);
    }
  },

  async createConversation(
    { commit, dispatch, state, rootState },
    { params, onSuccess, onFail, router } = {}
  ) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await dishaChatService.createConversation(params);
      let data = unwrapData(response);

      if (data && (params?.type === 'group' || data.type === 'group')) {
        const currentUserId = getUserId(getAuthUser(rootState));
        const creatorId = data.createdBy || params?.createdBy || currentUserId;
        data = {
          ...data,
          createdBy: creatorId,
          participants: (data.participants || []).map(p => {
            const pid = p.userId || p.user?.id || p.id;
            const isCreator = String(pid) === String(creatorId);
            return {
              ...p,
              role: p.role || (isCreator ? 'admin' : 'member')
            };
          })
        };
        const hasCreator = (data.participants || []).some(
          p => String(p.userId || p.user?.id || p.id) === String(creatorId)
        );
        if (creatorId && !hasCreator) {
          data.participants = [{ userId: creatorId, role: 'admin' }, ...(data.participants || [])];
        }
      }

      commit('setConversations', [data, ...state.conversations]);
      if (typeof onSuccess === 'function') onSuccess(data);
      return data;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to create conversation'
      });
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async sendMessage({ commit, dispatch }, { conversationId, params, onSuccess, onFail, router }) {
    try {
      const response = await dishaChatService.sendMessage(conversationId, params);
      const data = unwrapData(response);
      if (data) commit('messageReceived', data);
      if (typeof onSuccess === 'function') onSuccess(data);
      return data;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to send message'
      });
      throw e;
    }
  },

  async replyToMessage({ commit, dispatch }, { conversationId, params, onSuccess, onFail, router }) {
    try {
      const response = await dishaChatService.sendMessage(conversationId, params);
      const data = unwrapData(response);
      if (data) commit('messageReceived', data);
      if (typeof onSuccess === 'function') onSuccess(data);
      return data;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to reply to message'
      });
      throw e;
    }
  },

  async forwardMessage({ commit, dispatch }, { conversationId, params, onSuccess, onFail, router }) {
    try {
      const response = await dishaChatService.sendMessage(conversationId, params);
      const data = unwrapData(response);
      if (data) commit('messageReceived', data);
      if (typeof onSuccess === 'function') onSuccess(data);
      return data;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to forward message'
      });
      throw e;
    }
  },

  async deleteMessage(
    { commit, dispatch },
    { conversationId, messageId, onSuccess, onFail, router }
  ) {
    try {
      await dishaChatService.deleteMessage(conversationId, messageId);
      commit('markMessageDeleted', { conversationId, messageId });
      if (typeof onSuccess === 'function') onSuccess();
      return { conversationId, messageId };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to delete message'
      });
      throw e;
    }
  },

  async updateChatMessage(
    { commit, dispatch },
    { conversationId, messageId, params, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.updateMessage(conversationId, messageId, params);
      const payload = unwrapData(response);
      if (payload) {
        commit('messageReceived', { ...payload, conversationId, id: messageId });
      }
      if (typeof onSuccess === 'function') onSuccess(payload);
      return { conversationId, messageId, data: payload };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to update message'
      });
      throw e;
    }
  },

  async markAsRead({ commit, dispatch }, { conversationId, messageId, router }) {
    try {
      const response = await dishaChatService.markMessageRead(conversationId, messageId);
      commit('unreadUpdated', { conversationId, count: 0 });
      return { conversationId, data: unwrapData(response) };
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to mark message as read'
      });
      throw e;
    }
  },

  async addParticipant(
    { commit, dispatch },
    { conversationId, params, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.addParticipant(conversationId, params);
      const data = unwrapData(response);
      commit('updateParticipants', { conversationId, data });
      if (typeof onSuccess === 'function') onSuccess(data);
      return { conversationId, data };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to add participant'
      });
      throw e;
    }
  },

  async removeParticipant(
    { commit, dispatch },
    { conversationId, userId, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.removeParticipant(conversationId, userId);
      const data = unwrapData(response);
      commit('updateParticipants', { conversationId, data, userId });
      if (typeof onSuccess === 'function') onSuccess(data);
      return { conversationId, userId, data };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to remove participant'
      });
      throw e;
    }
  },

  async updateParticipantRole(
    { commit, dispatch },
    { conversationId, userId, role, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.updateParticipantRole(conversationId, userId, { role });
      const data = unwrapData(response);
      commit('updateParticipants', { conversationId, data, userId, role });
      if (typeof onSuccess === 'function') onSuccess(data);
      return { conversationId, userId, role, data };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to update participant role'
      });
      throw e;
    }
  },

  async searchParticipants(_ctx, { conversationId, query = '' }) {
    const response = await dishaChatService.searchParticipants(conversationId, query);
    return extractList(unwrapData(response));
  },

  async updateConversation(
    { commit, dispatch },
    { conversationId, params, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.updateConversation(conversationId, params);
      const payload = unwrapData(response);
      commit('updateConversationInList', payload);
      if (typeof onSuccess === 'function') onSuccess(payload);
      return payload;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to update conversation'
      });
      throw e;
    }
  },

  async leaveConversation({ commit, dispatch }, { conversationId, onSuccess, onFail, router }) {
    try {
      const response = await dishaChatService.leaveConversation(conversationId);
      commit('removeConversation', conversationId);
      if (typeof onSuccess === 'function') onSuccess(unwrapData(response));
      return { conversationId, data: unwrapData(response) };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to leave conversation'
      });
      throw e;
    }
  },

  async muteConversation(
    { commit, dispatch },
    { conversationId, muted = true, mutedUntil = null, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.muteConversation(conversationId, {
        muted,
        mutedUntil
      });
      const payload = unwrapData(response);
      commit('conversationUpdated', payload);
      if (typeof onSuccess === 'function') onSuccess(payload);
      return payload;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to mute conversation'
      });
      throw e;
    }
  },

  async togglePinChat(
    { commit, dispatch, state },
    { conversationId, pinned = true, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.pinConversation(conversationId, { pinned });
      const payload = unwrapData(response);
      const resolvedPinned = payload?.pinned ?? pinned;

      if (resolvedPinned) {
        if (!state.pinnedChatIds.includes(conversationId)) {
          commit('setPinnedChatIds', [conversationId, ...state.pinnedChatIds]);
        }
      } else {
        commit(
          'setPinnedChatIds',
          state.pinnedChatIds.filter(id => String(id) !== String(conversationId))
        );
      }

      commit('updateConversationInList', {
        id: conversationId,
        ...(payload && typeof payload === 'object' ? payload : {}),
        pinned: Boolean(resolvedPinned)
      });

      const result = {
        conversationId,
        pinned: resolvedPinned,
        conversation: payload
      };
      if (typeof onSuccess === 'function') onSuccess(result);
      return result;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to pin conversation'
      });
      throw e;
    }
  },

  async deleteConversation(
    { commit, dispatch },
    { conversationId, onSuccess, onFail, router }
  ) {
    try {
      await dishaChatService.deleteConversation(conversationId);
      commit('removeConversation', conversationId);
      if (typeof onSuccess === 'function') onSuccess(conversationId);
      return { conversationId };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to delete conversation'
      });
      throw e;
    }
  },

  async hideConversationForMe(
    { commit, dispatch },
    { conversationId, onSuccess, onFail, router }
  ) {
    try {
      try {
        await dishaChatService.hideConversation(conversationId);
      } catch {
        await dishaChatService.leaveConversation(conversationId);
      }
      commit('removeConversation', conversationId);
      if (typeof onSuccess === 'function') onSuccess(conversationId);
      return { conversationId };
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to hide conversation'
      });
      throw e;
    }
  },

  async sendChatNudge(
    { commit, dispatch, rootState },
    { conversationId, targetUserId, messageId, targetUser, onSuccess, onFail, router }
  ) {
    commit('setNudgeLoading', true);
    try {
      const response = await dishaChatService.nudge(conversationId, {
        targetUserId: targetUserId || targetUser?.id || targetUser?.userId,
        messageId: messageId || undefined
      });
      const payload = unwrapData(response);
      const authUser = getAuthUser(rootState);
      const targetName =
        targetUser?.firstName ||
        payload?.targetUser?.firstName ||
        'User';
      const systemMessage = {
        id: payload?.systemMessage?.id || `sys-nudge-${Date.now()}`,
        conversationId,
        type: 'system',
        body: payload?.systemMessage?.body || `You nudged ${targetName}.`,
        createdAt: new Date().toISOString(),
        senderId: getUserId(authUser),
        sender: authUser
      };
      commit('appendSystemMessage', systemMessage);
      const result = { conversationId, systemMessage };
      if (typeof onSuccess === 'function') onSuccess(result);
      return result;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to send nudge'
      });
      throw e;
    } finally {
      commit('setNudgeLoading', false);
    }
  },

  async notifyGroupMembers(
    { commit, dispatch },
    { conversationId, groupId, onSuccess, onFail, router }
  ) {
    const id = conversationId || groupId;
    commit('setNotifyLoading', true);
    try {
      const response = await dishaChatService.notify(id);
      const payload = unwrapData(response);
      const result = {
        groupId: id,
        notifiedCount: payload?.notifiedCount ?? payload?.count ?? 0
      };
      if (typeof onSuccess === 'function') onSuccess(result);
      return result;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to notify members'
      });
      throw e;
    } finally {
      commit('setNotifyLoading', false);
    }
  },

  async fetchMentions({ commit, dispatch }, { router, ...params } = {}) {
    commit('setMentionsLoading', true);
    try {
      const response = await dishaChatService.listMentions(params);
      commit('setMentions', extractList(unwrapData(response)));
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch mentions'
      });
    } finally {
      commit('setMentionsLoading', false);
    }
  },

  async sendAiMessage(
    { commit, dispatch, state, rootState },
    { message, onSuccess, onFail, router }
  ) {
    const currentConversationId = state.aiConversationId;
    const user = getAuthUser(rootState);
    const { firstName, lastName } = getUserDisplayParts(user);
    const userId = getUserId(user);
    const userPhoto = user?.photo?.path || '';

    commit('setAiLoading', true);

    const userMsg = {
      id: `user-${Date.now()}`,
      body: message,
      contentType: 'text',
      sender: { id: userId, firstName, lastName, photo: { path: userPhoto } },
      conversationId: currentConversationId,
      createdAt: new Date().toISOString()
    };
    commit('aiMessageReceived', userMsg);

    try {
      const response = await dishaChatService.sendAiMessage({
        conversationId: currentConversationId || undefined,
        message
      });
      const data = unwrapData(response);
      const isNewConversation = !currentConversationId && data.conversation_id;

      if (data.conversation_id) {
        commit('setAiConversationId', data.conversation_id);
      }

      const aiMsg = {
        id: `ai-${Date.now()}`,
        body: data.content || '',
        contentType: 'markdown',
        sender: { id: 'ai-assistant', firstName: 'DISHA', lastName: 'AI' },
        conversationId: data.conversation_id || currentConversationId,
        createdAt: new Date().toISOString()
      };
      commit('aiMessageReceived', aiMsg);

      if (isNewConversation) {
        dispatch('fetchAiConversations');
      }

      if (typeof onSuccess === 'function') onSuccess(data);
      return data;
    } catch (e) {
      commit('aiMessageReceived', {
        id: `ai-err-${Date.now()}`,
        body: 'AI service temporarily unavailable. Please try again.',
        contentType: 'markdown',
        sender: { id: 'ai-assistant', firstName: 'DISHA', lastName: 'AI' },
        conversationId: currentConversationId,
        createdAt: new Date().toISOString(),
        isError: true
      });

      if (typeof onFail === 'function') onFail(e.response || e);
      if (router) {
        dispatch('common/handleServiceException', { e, router }, { root: true });
      }
      throw e;
    } finally {
      commit('setAiLoading', false);
    }
  },

  async fetchAiConversations({ commit, dispatch }, { router, ...params } = {}) {
    commit('setAiConversationsLoading', true);
    try {
      const response = await dishaChatService.listAiConversations(params);
      const payload = unwrapData(response);
      commit('setAiConversations', Array.isArray(payload) ? payload : extractList(payload));
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch AI conversations'
      });
    } finally {
      commit('setAiConversationsLoading', false);
    }
  },

  async loadAiConversation(
    { commit, dispatch, rootState },
    { conversationId, onSuccess, onFail, router }
  ) {
    try {
      const response = await dishaChatService.getAiConversation(conversationId);
      const payload = unwrapData(response);
      const user = getAuthUser(rootState);
      const { firstName, lastName } = getUserDisplayParts(user);
      const userId = getUserId(user);
      const userPhoto = user?.photo?.path || '';

      const messages = (payload?.messages || []).map(m => ({
        id: m.id,
        body: m.content,
        contentType: 'markdown',
        sender:
          m.role === 'user'
            ? { id: userId, firstName, lastName, photo: { path: userPhoto } }
            : { id: 'ai-assistant', firstName: 'DISHA', lastName: 'AI' },
        conversationId: m.conversation_id,
        createdAt: m.created_at
      }));

      commit('setAiMessages', messages);
      commit('setAiConversationId', conversationId);

      if (typeof onSuccess === 'function') onSuccess(payload);
      return payload;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to load AI conversation'
      });
      throw e;
    }
  },

  async deleteAiConversation(
    { commit, dispatch, state },
    { conversationId, onSuccess, onFail, router }
  ) {
    try {
      await dishaChatService.deleteAiConversation(conversationId);
      commit('setAiConversations', state.aiConversations.filter(c => c.id !== conversationId));
      if (String(state.aiConversationId) === String(conversationId)) {
        commit('setAiMessages', []);
        commit('setAiConversationId', null);
      }
      if (typeof onSuccess === 'function') onSuccess(conversationId);
      return conversationId;
    } catch (e) {
      if (typeof onFail === 'function') onFail(e.response || e);
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to delete AI conversation'
      });
      throw e;
    }
  },

  async uploadChatFiles(_ctx, { files }) {
    return dishaChatService.uploadChatFiles(files);
  },

  async fetchTeamUsers({ commit, dispatch }, { router, limit = 1000 } = {}) {
    try {
      const response = await dishaChatService.listUsers({ page: 1, limit });
      commit('setTeamUsers', extractList(unwrapData(response)));
      return extractList(unwrapData(response));
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch team users'
      });
      return [];
    }
  },

  async fetchUnrestrictedUsers({ commit, dispatch }, { router, limit = 1000 } = {}) {
    try {
      const response = await dishaChatService.listUsers({
        page: 1,
        limit,
        noRestriction: true
      });
      commit('setUnrestrictedUsers', extractList(unwrapData(response)));
      return extractList(unwrapData(response));
    } catch (e) {
      handleActionError({ commit, dispatch }, {
        e,
        router,
        fallback: 'Failed to fetch unrestricted users'
      });
      return [];
    }
  },

  async fetchAiDraftReply({ commit, state }, { conversationId, messages = [] } = {}) {
    commit('setAiDraftLoading', true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1400));
      const sourceMessages = messages.length ? messages : state.messages[conversationId] || [];
      const lastIncoming = [...sourceMessages]
        .reverse()
        .find(m => m.type !== 'system' && m.type !== 'task');
      const name = lastIncoming?.sender?.firstName || 'there';
      const text = `Hi ${name},\n\nThanks for the update. I'll review this and get back to you with a response by end of day.\n\nBest regards`;
      commit('setAiDraftReplyText', text);
      return { conversationId, text };
    } finally {
      commit('setAiDraftLoading', false);
    }
  },

  async fetchAssistantSummary({ commit }) {
    commit('setAssistantLoading', true);
    try {
      await new Promise(resolve => setTimeout(resolve, 900));
      const data = {
        greeting: 'Good Morning',
        summary: {
          tasks: 3,
          forms: 2,
          audits: 1
        },
        items: [
          {
            id: 'a1',
            type: 'chat',
            title: 'Reply to John',
            subtitle: 'Pending response in Product Team',
            action: 'open'
          },
          {
            id: 'a2',
            type: 'form',
            title: 'Review Marketing Form',
            subtitle: 'Due today',
            action: 'open'
          },
          {
            id: 'a3',
            type: 'audit',
            title: 'Submit Store Audit',
            subtitle: 'Overdue by 1 day',
            action: 'open'
          }
        ]
      };
      commit('setAssistantData', data);
      return data;
    } finally {
      commit('setAssistantLoading', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
